import React, { memo, useState, useCallback, lazy, Suspense } from 'react';
import { Button, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

// Lazy load QR code component
const QRCode = lazy(() => import('react-qr-code'));
import {
    RiFileCopyLine,
    RiDownload2Fill,
    RiCheckLine,
    RiArrowRightUpBoxLine
} from 'react-icons/ri';

interface QRCodeSectionProps {
    missionaryLink: string;
}

const QRCodeSection = memo(({ missionaryLink }: QRCodeSectionProps) => {
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(missionaryLink);
            setShowCopySuccess(true);
            setTimeout(() => setShowCopySuccess(false), 3000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    }, [missionaryLink]);

    const downloadQRCode = useCallback(() => {
        try {
            const svg = document.getElementById('missionary-qrcode')?.querySelector('svg');
            if (!svg) {
                throw new Error('QR Code SVG not found');
            }

            // Create a canvas element
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Failed to get canvas context');
            }

            // Set canvas size (with padding)
            const padding = 20;
            canvas.width = svg.width.baseVal.value + padding * 2;
            canvas.height = svg.height.baseVal.value + padding * 2;

            // Fill white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Convert SVG to data URL
            const svgData = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            // Create image from SVG
            const img = new Image();
            img.onload = () => {
                // Draw image with padding
                ctx.drawImage(img, padding, padding);

                // Convert to PNG and download
                const pngUrl = canvas.toDataURL('image/png');
                const downloadLink = document.createElement('a');
                downloadLink.href = pngUrl;
                downloadLink.download = 'missionary-qr-code.png';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                // Cleanup
                URL.revokeObjectURL(svgUrl);
            };
            img.src = svgUrl;
        } catch (err) {
            console.error('Failed to download QR code:', err);
        }
    }, []);

    return (
        <div className="modern-qr-section">
            <div className="text-center mb-4">
                <h3 className="section-title mb-3">Share Your Page</h3>
                <p className="section-subtitle">
                    Scan this QR code or copy the link to share your missionary page
                </p>
            </div>

            <div className="d-flex flex-column align-items-center">
                {/* QR Code */}
                <div className="qr-code-container mb-4">
                    <div id="missionary-qrcode" className="qr-code-wrapper">
                        <Suspense fallback={<div style={{width: 200, height: 200, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Loading QR...</div>}>
                            <QRCode value={missionaryLink} size={200} level="H" className="qr-code" />
                        </Suspense>
                    </div>
                </div>

                {/* Link and Actions */}
                <div className="w-100">
                    <div className="link-container">
                        <div className="link-text">{missionaryLink}</div>
                        <div className="link-actions">
                            {/* Link Button */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Open link</Tooltip>}>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => window.open(missionaryLink, '_blank')}
                                    className="d-flex align-items-center"
                                >
                                    <RiArrowRightUpBoxLine />
                                </Button>
                            </OverlayTrigger>

                            {/* Copy Button */}
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>{showCopySuccess ? 'Copied!' : 'Copy link'}</Tooltip>
                                }
                            >
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="d-flex align-items-center"
                                >
                                    {showCopySuccess ? (
                                        <RiCheckLine className="text-success" />
                                    ) : (
                                        <RiFileCopyLine />
                                    )}
                                </Button>
                            </OverlayTrigger>

                            {/* Download Button */}
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Download QR Code</Tooltip>}
                            >
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={downloadQRCode}
                                    className="d-flex align-items-center"
                                >
                                    <RiDownload2Fill />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

// Add display name for debugging purposes
QRCodeSection.displayName = 'QRCodeSection';

export default QRCodeSection;
